import Constants from '../Constants';

import { HttpClient } from './HttpClient';
import {Domain} from "@zooplus/commons-lang";

type ActionName = 'Render' | 'RenderToken';
type TimerType = 'Success' | 'Timeout';

interface Metrics {
  metrics: Metric[];
}

interface Metric {
  productName: string;
  actionName: ActionName;
  componentName: string;
  componentLocation: string;
}

interface CounterMetric extends Metric {
  type: 'Init';
}

interface ErrorMetric extends Metric {
  type: 'Error';
  spendTime: number;
  errorType?: string;
}

interface TimerMetric extends Metric {
  type: TimerType;
  spendTime: number;
}

interface MetricsConfig {
  initTimestamp: number;
  componentName: string;
  componentLocation: string;
  siteDomain: Domain;
  customerId?: number;
  orderId?: number;
}

export default class MetricsService extends HttpClient {
  initTimestamp: number;
  componentName: string;
  productName: string;
  metricsBuffer: Metric[] = [];
  siteDomain: Domain;
  componentLocation: string;
  orderId: number | undefined;
  customerId: number | undefined;
  endpointErrorSent: boolean;

  constructor(config: MetricsConfig) {
    super();
    this.initTimestamp = config.initTimestamp;
    this.componentName = config.componentName;
    this.productName = Constants.PRODUCT_NAME;
    this.siteDomain = config.siteDomain;
    this.componentLocation = config.componentLocation;
    this.orderId = config.orderId;
    this.customerId = config.customerId;
    this.endpointErrorSent = false;
    setInterval(() => {
      if (this.metricsBuffer.length) {
        this.pushMetric();
      }
    }, Constants.RENDER_TIMEOUT);
  }

  static init(
    componentName: string,
    componentLocation: string,
    siteDomain: Domain,
    customerId: number | undefined = undefined
  ) {
    return new MetricsService({
      initTimestamp: Date.now(),
      componentName: componentName,
      componentLocation: componentLocation,
      siteDomain: siteDomain,
      customerId: customerId
    }).renderInit()
  }

  async pushMetricToApi() {
    const metrics: Metric[] = this.metricsBuffer;
    const metricsDto: Metrics = {
      metrics: metrics,
    };
    return super.post(Constants.API.URI.METRICS, metricsDto);
  }

  async pushMetric() {
    return new Promise<boolean>((resolve, reject) => {
      this.pushMetricToApi()
        .then((r) => {
          this.metricsBuffer = [];
          resolve(r.status === 200);
        })
        .catch(() => {
          this.metricsBuffer = [];
        });
    });
  }

  private getElapsedMillis(initTimestamp: number): number {
    return Date.now() - initTimestamp;
  }

  private setMetricsBuffer(metric: Metric) {
    this.metricsBuffer.push(metric);
    if (this.metricsBuffer.length >= 100) {
      this.pushMetric();
    }
  }

  firstEventToken(): MetricsService {
    const metric: TimerMetric = {
      type: 'Success',
      productName: this.productName,
      actionName: 'RenderToken',
      componentName: this.componentName,
      componentLocation: this.componentLocation,
      spendTime: this.getElapsedMillis(this.initTimestamp),
    };
    this.setMetricsBuffer(metric);
    return this;
  }

  renderInit(): MetricsService {
    const metric: CounterMetric = {
      type: 'Init',
      productName: this.productName,
      actionName: 'Render',
      componentName: this.componentName,
      componentLocation: this.componentLocation,
    };
    this.setMetricsBuffer(metric);
    return this;
  }

  renderSuccess(): MetricsService {
    const metric: TimerMetric = {
      type: 'Success',
      productName: this.productName,
      actionName: 'Render',
      componentName: this.componentName,
      componentLocation: this.componentLocation,
      spendTime: this.getElapsedMillis(this.initTimestamp),
    };
    this.setMetricsBuffer(metric);
    return this;
  }

  renderError(): MetricsService {
    const metric: ErrorMetric = {
      type: 'Error',
      productName: this.productName,
      actionName: 'Render',
      componentName: this.componentName,
      componentLocation: this.componentLocation,
      spendTime: this.getElapsedMillis(this.initTimestamp),
      errorType: 'RenderError',
    };
    this.setMetricsBuffer(metric);
    return this;
  }

  endpointError(): MetricsService {
    if (!this.endpointErrorSent) {
      this.endpointErrorSent = true;
      const metric: ErrorMetric = {
        type: 'Error',
        productName: this.productName,
        actionName: 'Render',
        componentName: this.componentName,
        componentLocation: this.componentLocation,
        spendTime: this.getElapsedMillis(this.initTimestamp),
        errorType: 'EndpointError',
      };
      this.setMetricsBuffer(metric);
    }
    return this;
  }

  tokenError(): MetricsService {
    if (!this.endpointErrorSent) {
      this.endpointErrorSent = true;
      const metric: ErrorMetric = {
        type: 'Error',
        productName: this.productName,
        actionName: 'Render',
        componentName: this.componentName,
        componentLocation: this.componentLocation,
        spendTime: this.getElapsedMillis(this.initTimestamp),
        errorType: 'TokenTimeoutError',
      };
      this.setMetricsBuffer(metric);
    }
    return this;
  }
}
